import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage as FM } from 'react-intl';
import axios from 'axios';
import { APP_API_URL } from 'utils';
import { PageContentPreloader } from 'common/components';

import DocumentItem from './_components/DocumentItem';
import { staticStyles } from './style';

const Document = ({ userId }) => {
  const [documents, setDocuments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileContents, setFileContents] = useState({});
  const [fileTypes, setFileTypes] = useState({});

  const documentTypes = [
    { key: 'passport', titleId: 'boCopyOfPassportWithResidAddress' },
    { key: 'passport_with_face', titleId: 'proofOfLife' },
    { key: 'address_proof', titleId: 'proofResidAddress' },
    { key: 'identification_proof', titleId: 'proofOfIdent' },
    { key: 'additional_document', titleId: 'additionalDocumentCaption' },
    { key: 'payment_proof', titleId: 'payment_proof' },
  ];

  const fetchFileContent = async docId => {
    // Always fetch the latest file content, don't use cache
    try {
      const response = await axios.get(`${APP_API_URL}/backoffice/user/${userId}/document/${docId}/file/`, {
        responseType: 'blob',
      });
      // When responseType is 'blob', axios returns the blob directly in response.data
      const blob = response.data;
      const fileUrl = URL.createObjectURL(blob);
      // Save the file type (MIME type)
      const fileType = blob.type;
      setFileContents(prev => ({ ...prev, [docId]: fileUrl }));
      setFileTypes(prev => ({ ...prev, [docId]: fileType }));
    } catch (err) {
      console.error('Error fetching file:', err);
    }
  };

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${APP_API_URL}/backoffice/user/${userId}/document/list/contacts/`);

        if (response.status === 403 || response.status === 401 || response.status === 404) {
          setError('permission_denied');
          setDocuments(null);
        } else if (response.data) {
          // Filter out rejected documents and document types with only rejected documents
          const filteredDocuments = {};
          Object.keys(response.data).forEach(key => {
            const nonRejectedDocs = response.data[key].filter(doc => doc.status !== 'rejected');
            // Only include document types that have non-rejected documents
            if (nonRejectedDocs.length > 0) {
              filteredDocuments[key] = nonRejectedDocs;
            }
          });
          setDocuments(filteredDocuments);
          setError(null);
          // Auto-load all non-rejected documents
          Object.keys(filteredDocuments).forEach(key => {
            if (filteredDocuments[key] && filteredDocuments[key].length > 0) {
              filteredDocuments[key].forEach(doc => {
                fetchFileContent(doc.id);
              });
            }
          });
        }
      } catch (err) {
        if (
          err.response &&
          (err.response.status === 403 || err.response.status === 401 || err.response.status === 404)
        ) {
          setError('permission_denied');
        } else {
          setError('fetch_error');
        }
        setDocuments(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [userId]);

  const renderDocumentItem = (doc, index) => {
    // Skip rendering if document status is rejected
    if (doc.status === 'rejected') {
      return null;
    }

    const fileUrl = fileContents[doc.id];
    // 经测试这里fileType是正确的
    const fileType = fileTypes[doc.id] || '';

    // Define file type extensions based on mime_type
    const imageExtensions = {
      'image/png': '.png',
      'image/jpeg': '.jpeg',
      'image/jpg': '.jpg',
      'image/gif': '.gif',
      'image/bmp': '.bmp',
      'image/webp': '.webp',
    };

    const documentExtensions = {
      'application/pdf': '.pdf',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    };

    // Determine file type based on fileType, mime_type, or file extension
    const isImage = fileType in imageExtensions;
    const isPDF = fileType === 'application/pdf';
    const isWord = fileType in documentExtensions && !isPDF;

    return (
      <DocumentItem
        key={`doc-${doc.id}-${index}`}
        doc={doc}
        fileUrl={fileUrl}
        isImage={isImage}
        isPDF={isPDF}
        isWord={isWord}
      />
    );
  };

  if (loading) {
    return (
      <PageContentPreloader ready={false} type="smallLines">
        <div />
      </PageContentPreloader>
    );
  }

  // 移除之前的提前返回逻辑，让组件继续渲染显示错误信息
  const hasAnyDocuments = Object.values(documents).some(docs => docs && docs.length > 0);

  if (!hasAnyDocuments) {
    return (
      <div className="Document ContactPageInfoBlock">
        <div className="Document__message">
          <FM id="boNoDocument" />
        </div>
      </div>
    );
  }

  return (
    <div className="Document ContactPageInfoBlock">
      {error && (
        <div className="Document__message">
          {error === 'permission_denied' ? <FM id="noPermissionsErr" /> : <FM id="boNoDocument" />}
        </div>
      )}

      <div className="ContactPageInfoBlock__TitleWrap">
        <h3 className="ContactPageInfoBlock__Title">
          <FM id="boUserDocuments" />
        </h3>
      </div>

      <div className="ContactPageInfoBlock__ContentWrap">
        {documentTypes.map(({ key, titleId }) => {
          const docs = documents[key];
          if (!docs || docs.length === 0) return null;

          return (
            <div key={key} className="Document__section">
              <div className="ContactPageInfoBlock__TitleWrap">
                <FM id={titleId}>{txt => <span className="ContactPageInfoBlock__ItemTitle">{txt}</span>}</FM>
              </div>
              <div className="Document__small-images-wrap">
                <div className="Document__small-images">{docs.map((doc, index) => renderDocumentItem(doc, index))}</div>
              </div>
            </div>
          );
        })}
      </div>
      <style jsx>{staticStyles}</style>
    </div>
  );
};

Document.propTypes = {
  userId: PropTypes.number.isRequired,
};

export default Document;
